import { useEffect, useRef } from 'react';

/** What the portal learns about a mailbox change: never any mail content. */
export interface MailChange {
  /** The mailbox that changed, or '' when the tab simply came back to the foreground. */
  mailboxId: string;
  /** 'inbound' (mail arrived), 'outbound' (a message was sent), or 'resume' (tab became visible again). */
  kind: string;
}

export type MailSubscribeFn = (
  mailboxId: string,
  onChange: (kind: string) => void,
  onError: (error: Error) => void,
) => () => void;

/**
 * Listens to the content-free `mail_notify/{mailboxId}` document the backend touches whenever mail
 * arrives or is sent (firestore.rules: portal staff who are members of the mailbox may read it).
 * The first snapshot only records the current state, so opening the page never counts as a change.
 * The Firestore SDK is loaded on first use, like the verification view.
 */
export const firestoreMailSubscribe: MailSubscribeFn = (mailboxId, onChange, onError) => {
  let unsubscribe: () => void = () => {};
  let cancelled = false;
  Promise.all([import('firebase/firestore'), import('../config/firestore')])
    .then(([fs, { getDb }]) => {
      if (cancelled) return;
      let baseline: string | null | undefined;
      unsubscribe = fs.onSnapshot(
        fs.doc(getDb(), 'mail_notify', mailboxId),
        (snapshot) => {
          const data = snapshot.exists() ? (snapshot.data() as { nonce?: string; kind?: string }) : null;
          const nonce = data?.nonce ?? null;
          if (baseline === undefined) {
            baseline = nonce;
            return;
          }
          if (nonce === baseline) return;
          baseline = nonce;
          if (data) onChange(data.kind ?? 'inbound');
        },
        (error) => onError(error),
      );
    })
    .catch((error) => onError(error instanceof Error ? error : new Error(String(error))));
  return () => {
    cancelled = true;
    unsubscribe();
  };
};

/** Changes arriving within this window are delivered once per mailbox. */
const DEBOUNCE_MS = 400;
/** A tab hidden for at least this long refreshes when it becomes visible again. */
const RESUME_AFTER_MS = 60_000;

/**
 * Calls `onChange` when mail arrives or is sent in any of the mailboxes, and when a long-hidden tab
 * returns to the foreground (a safety net if a listener was throttled or blocked). A listener that
 * fails (for example before the rules are deployed) is ignored: the page keeps working, it just
 * does not update by itself.
 */
export function useMailLive(
  mailboxIds: readonly string[],
  onChange: (change: MailChange) => void,
  subscribe: MailSubscribeFn = firestoreMailSubscribe,
): void {
  const handler = useRef(onChange);
  handler.current = onChange;
  const key = [...mailboxIds].sort().join(',');

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    const pending = new Map<string, string>();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      timer = undefined;
      for (const [mailboxId, kind] of pending) handler.current({ mailboxId, kind });
      pending.clear();
    };
    const queue = (mailboxId: string, kind: string) => {
      pending.set(mailboxId, kind);
      if (timer === undefined) timer = setTimeout(flush, DEBOUNCE_MS);
    };

    const unsubscribers = ids.map((id) =>
      subscribe(
        id,
        (kind) => queue(id, kind),
        () => undefined,
      ),
    );

    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= RESUME_AFTER_MS) {
        handler.current({ mailboxId: '', kind: 'resume' });
      }
      hiddenAt = null;
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) clearTimeout(timer);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [key, subscribe]);
}
